import React, { useEffect } from 'react';
import { Form, Input, InputNumber, Modal, Select, Switch, notification } from 'antd';
import {
	IShelfWithLocations,
	useCreateShelfMutation,
	useUpdateShelfMutation,
} from '../../../../redux/api/inventory';
import { PRODUCTION_METHODS } from '../../constants/inventory';

interface Props {
	open: boolean;
	rackId?: string;
	shelf?: IShelfWithLocations | null;
	onClose: () => void;
}

const ShelfFormModal: React.FC<Props> = ({ open, rackId, shelf, onClose }) => {
	const [form] = Form.useForm();
	const isEdit = !!shelf;

	const [createShelf, { isLoading: creating }] = useCreateShelfMutation();
	const [updateShelf, { isLoading: updating }] = useUpdateShelfMutation();

	useEffect(() => {
		if (!open) return;
		if (shelf) {
			form.setFieldsValue({
				name: shelf.name,
				max_weight: shelf.max_weight,
				production_method_type: shelf.production_method_type ?? undefined,
				description: shelf.description,
				reservations_enabled: shelf.reservations_enabled !== false,
			});
		} else {
			form.resetFields();
			form.setFieldsValue({ max_weight: 800, reservations_enabled: true });
		}
	}, [open, shelf, form]);

	const handleOk = async () => {
		const values = await form.validateFields();
		// material_type and shape_category are intentionally omitted: these
		// partially implemented restriction fields are hidden from the form
		// (PTB-5924). Omitting them keeps the PATCH partial so existing shelf
		// values are preserved and the model/backend stay untouched.
		const body = {
			name: values.name,
			max_weight: values.max_weight ?? null,
			production_method_type: values.production_method_type ?? null,
			description: values.description ?? '',
			reservations_enabled: values.reservations_enabled !== false,
		};
		try {
			if (isEdit && shelf) {
				await updateShelf({ id: shelf.id, body }).unwrap();
				notification.success({ message: `Shelf ${values.name} updated` });
			} else {
				await createShelf({ rack: rackId as string, ...body }).unwrap();
				notification.success({ message: `Shelf ${values.name} created` });
			}
			onClose();
		} catch (err: any) {
			notification.error({
				message: 'Could not save shelf',
				description: err?.data?.detail || 'Please review the form and try again.',
			});
		}
	};

	return (
		<Modal
			title={isEdit ? `Edit ${shelf?.name}` : 'Add Shelf'}
			open={open}
			onCancel={onClose}
			onOk={handleOk}
			confirmLoading={creating || updating}
			okText={isEdit ? 'Save' : 'Create'}
			destroyOnClose
		>
			<Form form={form} layout="vertical">
				<Form.Item
					name="name"
					label="Shelf Name"
					rules={[{ required: true, message: 'Shelf name is required' }]}
				>
					<Input placeholder="e.g. Shelf 1" />
				</Form.Item>
				<Form.Item
					name="max_weight"
					label="Max Weight (lb)"
					rules={[{ required: true, message: 'Max weight is required' }]}
				>
					<InputNumber style={{ width: '100%' }} min={0} placeholder="e.g. 800" />
				</Form.Item>
				<Form.Item name="production_method_type" label="Production Method">
					<Select allowClear options={PRODUCTION_METHODS} placeholder="Any" />
				</Form.Item>
				<Form.Item name="description" label="Description">
					<Input.TextArea rows={2} />
				</Form.Item>
				<Form.Item
					name="reservations_enabled"
					label="Allow reservations"
					valuePropName="checked"
					extra="Turn off to keep material on this shelf out of reservation. A rack with reservations off already blocks every shelf on it."
				>
					<Switch />
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default ShelfFormModal;
